import React from 'react';
import { ModelTreeEditor, Toolbar } from '@leverege/ui-plugin';
import { Content, Pane, Dialogs } from '@leverege/ui-elements';
import { GlobalState, Selection } from '@leverege/ui-redux';
import DeleteDialog from "../deleteDialog/DeleteDialog.js";
import 'rc-tree/assets/index.css';
import Pagination from "../pagination/Pagination.js";
import RsrcModel from "../../models/Rsrc.js";
import DefaultNodeRenderer from "./NodeRenderer.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const TYPE = 'rsrc.listRenderer.tree';
const matchContext = {
  client: TYPE,
  objectType: RsrcModel.TYPE
};
const NODE_TYPE = 'rsrc.listRenderer.tree.node';
const nodeMatchContext = {
  client: NODE_TYPE,
  objectType: RsrcModel.TYPE
};
function RolloverToolbar(props) {
  const {
    isRollover,
    data,
    targeted
  } = props;
  if (isRollover(data)) {
    return /*#__PURE__*/_jsx(Toolbar, {
      variant: "tertiarySmall",
      prefer: "icon",
      matchContext: {
        ...nodeMatchContext,
        use: 'rolloverMenu'
      },
      context: {
        clientProps: props,
        target: targeted?.items
      }
    });
  }
  return /*#__PURE__*/_jsx("div", {
    style: {
      height: '25px'
    }
  });
}
class RsrcTree extends React.Component {
  componentDidMount() {
    document.addEventListener('keyup', this.onKeyPressed);
  }
  componentWillUnmount() {
    document.removeEventListener('keyup', this.onKeyPressed);
  }
  collapseTree = (tree, isRoot) => {
    const toReturn = [];
    // console.log( tree )
    tree.forEach(rootItem => {
      if (!rootItem.data && rootItem.children.length === 1) {
        toReturn.push(this.collapseTree([{
          ...rootItem.children[0],
          name: `${rootItem.name}/${rootItem.children[0].name}`
        }]));
      } else {
        const children = this.collapseTree(rootItem.children);
        toReturn.push({
          ...rootItem,
          children: Array.isArray(children) ? children : [children]
        });
      }
    });
    if (!isRoot && toReturn.length === 1) {
      return toReturn[0];
    }
    return toReturn;
  };
  getTreeData = () => {
    const {
      paginator
    } = this.props;
    const items = paginator?.pageItems;

    /**
     * key, title, children
     */
    const tree = [];
    items.forEach(item => {
      if (!item) {
        return;
      }
      const pathParts = item.data.path.split('/');
      let nTree = tree;
      pathParts.forEach((pPart, index) => {
        const pPrefix = pathParts.slice(0, index + 1).join('/');
        let match = nTree.find(n => n.id === pPrefix);
        if (!match) {
          match = {
            id: pPrefix,
            name: pathParts[index],
            children: [],
            data: index === pathParts.length - 1 ? item : null,
            canDelete: index === pathParts.length - 1
          };
          nTree.push(match);
        }
        nTree = match.children;
      });
    });
    return this.collapseTree(tree, true);
  };
  getRsrc(node) {
    return node?.data || {
      type: 'rsrc',
      ref: node?.key,
      data: {
        path: node?.key
      }
    };
  }
  onSelect = async evt => {
    const {
      selectionKey,
      resourceApi
    } = this.props;
    const selected = this.getRsrc(evt?.node);
    if (selected.data?.path) {
      await GlobalState.dispatch(resourceApi.rsrc(selected.data.path).get());
    }
    GlobalState.dispatch(Selection.set(selectionKey, [selected]));
  };
  onChange = evt => {
    console.log('on change', evt);
  };
  getContext = () => {
    const {
      targeted
    } = this.props;
    return {
      clientProps: this.props,
      target: targeted?.items
    };
  };
  removeItem = (tree, removed) => {
    const {
      reload,
      resourceApi
    } = this.props;
    if (removed.data) {
      Dialogs.show({
        component: DeleteDialog,
        props: {
          rsrc: [removed.data],
          onDelete: async value => {
            for (let i = 0; i < value.length; i++) {
              // eslint-disable-next-line no-await-in-loop
              await GlobalState.dispatch(resourceApi.rsrc(RsrcModel.getPath(value[i])).delete());
            }
            await reload();
          }
        }
      });
    }
    return tree;
  };
  onRollover = evt => {
    const {
      rolloverKey
    } = this.props;
    const rollover = this.getRsrc(evt?.node);
    GlobalState.dispatch(Selection.add(rolloverKey, rollover));
  };
  offRollover = evt => {
    const {
      rolloverKey
    } = this.props;
    const rollover = this.getRsrc(evt?.node);
    GlobalState.dispatch(Selection.remove(rolloverKey, rollover));
  };
  isRollover = node => {
    const {
      rollover
    } = this.props;
    return !!rollover?.items?.find(i => RsrcModel.getPath(i) === node.key);
  };
  getNodeProps() {
    const {
      rollover,
      selection,
      targeted,
      paginator,
      reload,
      resourceApi
    } = this.props;
    return {
      isRollover: this.isRollover,
      rollover,
      selection,
      targeted,
      paginator,
      reload,
      resourceApi
    };
  }
  setItem = (modelValue, newValue) => {
    const {
      resourceApi,
      paginator
    } = this.props;
    const items = paginator?.pageItems;
    const item = items?.find(item => item.id === newValue.id);
    // kick off the save and then return modelValue
    if (!item.data) {
      return null;
    }
    const patch = [];
    Object.entries(newValue.data).forEach(([path, value]) => {
      if (value !== item.data[path]) {
        patch.push({
          path,
          value
        });
      }
    });
    GlobalState.dispatch(resourceApi.rsrc(RsrcModel.getPath(item)).update(patch));
    return modelValue;
  };
  render() {
    const {
      paginator,
      selection
    } = this.props;
    const context = this.getContext();
    const treeData = this.getTreeData();
    let selected = {};
    if (selection?.items?.[0]) {
      selected = {
        key: selection.items[0].ref,
        data: selection.items[0]
      };
    }
    return /*#__PURE__*/_jsxs(Content, {
      children: [/*#__PURE__*/_jsx(Content.Header, {
        children: /*#__PURE__*/_jsxs(Pane, {
          layout: "flex:rowSpaceBetween",
          children: [/*#__PURE__*/_jsx(Toolbar, {
            variant: "actionBar",
            prefer: "icon",
            hasContextMenu: true,
            contextMenuHideWhenEmpty: true,
            matchContext: {
              ...matchContext,
              use: 'actionBarLeft'
            },
            context: context
          }), /*#__PURE__*/_jsx(Toolbar, {
            variant: "actionBar",
            prefer: "icon",
            matchContext: {
              ...matchContext,
              use: 'actionBarRight'
            },
            context: context
          })]
        })
      }), /*#__PURE__*/_jsx(Content.Area, {
        children: /*#__PURE__*/_jsx(ModelTreeEditor, {
          model: {
            getTree: val => val,
            remove: this.removeItem,
            set: this.setItem
          },
          onSelect: this.onSelect,
          selected: selected,
          selectControlled: true,
          onChange: this.onChange,
          value: treeData,
          nodeProps: this.getNodeProps(),
          postTitleComponent: RolloverToolbar,
          nodeRenderer: DefaultNodeRenderer,
          treeProps: {
            onMouseEnter: this.onRollover,
            onMouseLeave: this.offRollover
          }
        })
      }), /*#__PURE__*/_jsx(Content.Footer, {
        children: /*#__PURE__*/_jsx(Pagination, {
          paginator: paginator,
          matchContext: matchContext,
          context: context
        })
      })]
    });
  }
}
RsrcTree.TYPE = TYPE;
RsrcTree.PLUGIN = {
  type: TYPE,
  component: RsrcTree
};
export default RsrcTree;